"use client";

import {
	buildClientConsentRecordFromServerStates,
	CMP_CONSENT_STORAGE_VERSION,
	type ConsentPolicyCategory,
	latestServerCmpDecidedAt,
} from "@repo/identity";
import Cookies from "js-cookie";
import type { ConsentRecord } from "./constants";
import { CONSENT_COOKIE_NAME, CONSENT_LOCALSTORAGE_KEY } from "./constants";
import {
	persistConsentRecord,
	readConsentLocalStorage,
	removeLegacyConsentCookie,
} from "./storage-client";

export interface EffectiveConsentApiState {
	category: string;
	granted: boolean;
	decidedAt: string;
}

type ConsentRecordListener = (record: ConsentRecord | null) => void;

let consentRecordListener: ConsentRecordListener | null = null;

export function registerConsentRecordListener(
	listener: ConsentRecordListener | null,
): void {
	consentRecordListener = listener;
}

function publishConsentRecord(record: ConsentRecord | null): void {
	consentRecordListener?.(record);
}

export function clearSiteCmpConsentClientState(): void {
	if (typeof window === "undefined") {
		return;
	}
	removeLegacyConsentCookie();
	Cookies.remove(CONSENT_COOKIE_NAME, { path: "/" });
	try {
		window.localStorage.removeItem(CONSENT_LOCALSTORAGE_KEY);
	} catch {
		// Ignore storage failures.
	}
	publishConsentRecord(null);
}

export function reconcileLocalCmpFromServer(params: {
	effectiveConsent: EffectiveConsentApiState[];
	localRecord: ConsentRecord | null;
}): ConsentRecord | null {
	if (params.effectiveConsent.length === 0) {
		return params.localRecord;
	}

	const serverStates = params.effectiveConsent.map((state) => ({
		category: state.category as ConsentPolicyCategory,
		granted: state.granted,
		decidedAt: state.decidedAt,
	}));
	const serverRecord = buildClientConsentRecordFromServerStates(
		serverStates,
		CMP_CONSENT_STORAGE_VERSION,
	);

	if (params.localRecord == null) {
		return serverRecord;
	}

	const serverDecidedAt = latestServerCmpDecidedAt(serverStates);
	if (serverDecidedAt > params.localRecord.ts) {
		return serverRecord;
	}

	return params.localRecord;
}

export function applyServerCmpConsentRecord(record: ConsentRecord): void {
	persistConsentRecord(record);
	publishConsentRecord(record);
}

export function readCurrentCmpConsentRecord(): ConsentRecord | null {
	return readConsentLocalStorage();
}
